// src/pages/GfmDeliveries.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCommands } from '../api/commands';
import PageHeader from '../components/PageHeader';
import SupplierSelect from '../components/SupplierSelect';
import Icon from '../components/Icon';
import { GFM_SITES, siteLabel, matchSupplier } from '../constants/gfm';

export default function GfmDeliveries(){
  const cmd = useCommands();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [filter, setFilter] = useState('pending');
  const [q, setQ] = useState('');

  // mark-delivered modal
  const [active, setActive] = useState(null);
  const [confirmRef, setConfirmRef] = useState('');
  const [recipient, setRecipient] = useState('');

  // edit modal
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ supplier: '', orderedBy: '', site: 'main', notes: '', orderedDate: '', lines: [] });

  async function load(){
    setLoading(true);
    setErr(''); setOk('');
    try {
      const data = await (cmd?.gfmDeliveries?.list
        ? cmd.gfmDeliveries.list()
        : cmd.deliveries.listGfm());
      setRows(Array.isArray(data) ? data : []);
    } catch (e){
      setErr(e?.response?.data?.error || e?.message || 'Failed to load deliveries');
    } finally { setLoading(false); }
  }

  useEffect(()=>{ load(); },[]); // eslint-disable-line react-hooks/exhaustive-deps

  const pending = useMemo(()=>rows.filter(d=>!d.deliveredDate),[rows]);
  const delivered = useMemo(()=>rows.filter(d=>!!d.deliveredDate),[rows]);
  const shown = useMemo(()=>{
    const list = filter === 'pending' ? pending : delivered;
    const term = q.trim().toLowerCase();
    if (!term) return list;
    return list.filter(d =>
      [d.supplier, d.orderedBy, d.recipient, siteLabel(d.site)].some(v => String(v||'').toLowerCase().includes(term)) ||
      (Array.isArray(d.lines) ? d.lines : []).some(l => String(l.item||'').toLowerCase().includes(term))
    );
  }, [pending, delivered, filter, q]);

  // ── Mark delivered
  function openDeliverModal(d){ setActive(d); setConfirmRef(''); setRecipient(''); }
  function closeDeliverModal(){ setActive(null); }

  async function markDelivered() {
    if (!active) return;
    try {
      if (cmd?.gfmDeliveries?.markDelivered) {
        await cmd.gfmDeliveries.markDelivered(active._id, { recipient, confirmation: confirmRef });
      }
      await load();
      closeDeliverModal();
      setOk('Delivery marked and added to inventory');
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Failed to mark delivered');
    }
  }

  // ── Edit
  function openEditModal(d) {
    setEditTarget(d);
    setEditForm({
      supplier:    matchSupplier(d.supplier) || d.supplier || '',
      orderedBy:   d.orderedBy   || '',
      site:        d.site        || 'main',
      notes:       d.notes       || '',
      orderedDate: d.orderedDate ? new Date(d.orderedDate).toISOString().slice(0, 10) : '',
      lines: Array.isArray(d.lines)
        ? d.lines.map(l => ({ item: l.item || '', amount: Number(l.amount) || 1, unitCost: Number(l.unitCost) || 0 }))
        : [],
    });
  }
  function closeEditModal(){ setEditTarget(null); }

  function setLine(i, field, value) {
    setEditForm(f => {
      const lines = [...f.lines];
      lines[i] = { ...lines[i], [field]: value };
      return { ...f, lines };
    });
  }

  async function saveEdit() {
    if (!editTarget) return;
    setErr('');
    try {
      await cmd.gfmDeliveries.update(editTarget._id, editForm);
      await load();
      closeEditModal();
      setOk('Order updated');
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Failed to update delivery');
    }
  }

  // ── Delete
  async function deleteDelivery(d) {
    if (!window.confirm(`Cancel and delete this order from ${d.supplier || 'unknown supplier'}? This cannot be undone.`)) return;
    setErr('');
    try {
      await cmd.gfmDeliveries.delete(d._id);
      await load();
      setOk('Order deleted');
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Failed to delete delivery');
    }
  }

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '—';

  return (
    <div className="container">
      <PageHeader
        eyebrow="GFM"
        title="Orders"
        actions={
          <>
            <Link to="/inventory/main" className="btn secondary">Inventory</Link>
            <button className="btn" onClick={()=>nav('/gfm/deliveries/new')}>
              <Icon name="plus" size={15} /> New order
            </button>
          </>
        }
      />

      {(err || ok) && (
        <div className="page-alerts">
          {err && <div className="alert error">{err}</div>}
          {ok && <div className="alert success">{ok}</div>}
        </div>
      )}

      <div className="card">
        <div className="toolbar mb-16">
          <div className="segmented">
            <button className={filter==='pending'?'seg active':'seg'} onClick={()=>setFilter('pending')}>
              Pending <span className="seg-count">{pending.length}</span>
            </button>
            <button className={filter==='delivered'?'seg active':'seg'} onClick={()=>setFilter('delivered')}>
              Delivered <span className="seg-count">{delivered.length}</span>
            </button>
          </div>
          <input className="input grow" placeholder="Search items, suppliers, people" value={q} onChange={e=>setQ(e.target.value)} />
          <button className="btn secondary" onClick={load}>Reload</button>
        </div>

        {loading ? <div className="empty">Loading</div> : shown.length === 0 ? (
          <div className="empty">No {filter} orders.</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Site</th>
                  <th>Items</th>
                  <th>Supplier</th>
                  <th>Ordered by</th>
                  <th>Ordered</th>
                  <th>Delivered</th>
                  <th className="actions"></th>
                </tr>
              </thead>
              <tbody>
                {shown.map(d=>(
                  <tr key={d._id}>
                    <td><span className={`site-pill ${d.site || ''}`}>{siteLabel(d.site) || '—'}</span></td>
                    <td>
                      <ul className="line-list">
                        {(Array.isArray(d.lines)?d.lines:[]).map((l,i)=>(
                          <li key={`${d._id}-${i}`}>{l.item} <span className="qty">× {l.amount}</span></li>
                        ))}
                      </ul>
                    </td>
                    <td>{d.supplier || '—'}</td>
                    <td>{d.orderedBy || '—'}</td>
                    <td className="nowrap">{fmtDate(d.orderedDate)}</td>
                    <td className="nowrap">{fmtDate(d.deliveredDate)}</td>
                    <td className="actions">
                      {!d.deliveredDate ? (
                        <div className="row-actions">
                          <button className="btn secondary small" onClick={()=>openEditModal(d)}>Edit</button>
                          <button className="btn secondary small destructive" onClick={()=>deleteDelivery(d)}>Delete</button>
                          <button className="btn small" onClick={()=>openDeliverModal(d)}>Mark delivered</button>
                        </div>
                      ) : (
                        <span className="badge ok">Delivered</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mark delivered modal */}
      {active && (
        <div className="modal-backdrop" onClick={closeDeliverModal}>
          <div className="modal" role="dialog" aria-modal="true" onClick={e=>e.stopPropagation()}>
            <div className="modal-head">
              <h3>Mark as delivered</h3>
              <p className="modal-sub">
                Stock will be added to {siteLabel(active.site) || 'Main'}
                {active.supplier ? `, from ${active.supplier}` : ''}.
              </p>
            </div>
            <div className="card inset mb-16">
              <ul className="line-list">
                {(Array.isArray(active.lines)?active.lines:[]).map((l,i)=>(
                  <li key={`${active._id}-line-${i}`}>{l.item} <span className="qty">× {l.amount}</span></li>
                ))}
              </ul>
            </div>
            <div className="form-grid cols-2">
              <label className="input-label">Delivery reference
                <input className="input" value={confirmRef} onChange={e=>setConfirmRef(e.target.value)} />
              </label>
              <label className="input-label">Recipient
                <input className="input" value={recipient} onChange={e=>setRecipient(e.target.value)} />
              </label>
            </div>
            <div className="modal-foot">
              <button className="btn secondary" onClick={closeDeliverModal}>Cancel</button>
              <button className="btn" onClick={markDelivered}>Confirm delivery</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editTarget && (
        <div className="modal-backdrop" onClick={closeEditModal}>
          <div className="modal lg" role="dialog" aria-modal="true" onClick={e=>e.stopPropagation()}>
            <div className="modal-head">
              <h3>Edit order</h3>
            </div>

            <div className="form-grid cols-2">
              <label className="input-label">Supplier
                <SupplierSelect value={editForm.supplier}
                  onChange={v=>setEditForm(f=>({...f, supplier: v}))} />
              </label>
              <label className="input-label">Ordered by
                <input className="input" value={editForm.orderedBy}
                  onChange={e=>setEditForm(f=>({...f, orderedBy: e.target.value}))} />
              </label>
              <label className="input-label">Site
                <select className="input" value={editForm.site}
                  onChange={e=>setEditForm(f=>({...f, site: e.target.value}))}>
                  {GFM_SITES.map(s=><option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </label>
              <label className="input-label">Order date
                <input type="date" className="input" value={editForm.orderedDate}
                  onChange={e=>setEditForm(f=>({...f, orderedDate: e.target.value}))} />
              </label>
              <label className="input-label span-all">Notes
                <input className="input" value={editForm.notes}
                  onChange={e=>setEditForm(f=>({...f, notes: e.target.value}))} />
              </label>
            </div>

            <div className="mt-16">
              <div className="card-head" style={{marginBottom:8}}>
                <span className="input-label" style={{margin:0}}>Items</span>
                <button className="btn secondary small"
                  onClick={()=>setEditForm(f=>({...f, lines:[...f.lines,{item:'',amount:1,unitCost:0}]}))}>
                  <Icon name="plus" size={14} /> Add line
                </button>
              </div>
              <div className="lines-editor">
                {editForm.lines.length > 0 && (
                  <div className="lines-editor-row lines-editor-head">
                    <span>Item</span><span>Qty</span><span>Unit cost</span><span />
                  </div>
                )}
                {editForm.lines.map((line, i)=>(
                  <div key={i} className="lines-editor-row">
                    <input className="input" placeholder="Item" value={line.item}
                      onChange={e=>setLine(i,'item',e.target.value)} />
                    <input type="number" className="input" placeholder="Qty" value={line.amount} min={1}
                      onChange={e=>setLine(i,'amount',Number(e.target.value))} />
                    <div className="input-wrap"><span className="prefix">£</span>
                      <input type="number" className="input" value={line.unitCost} min={0} step={0.01}
                        onChange={e=>setLine(i,'unitCost',Number(e.target.value))} />
                    </div>
                    <button className="btn ghost small icon" aria-label={`Remove ${line.item || 'line'}`}
                      onClick={()=>setEditForm(f=>({...f, lines:f.lines.filter((_,j)=>j!==i)}))}>
                      <Icon name="x" size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-foot">
              <button className="btn secondary" onClick={closeEditModal}>Cancel</button>
              <button className="btn" onClick={saveEdit}>Save changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
